import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const categoryComics = category ? comics.filter((c) => c.category === category.name) : comics;

  const featured = categoryComics.find((c) => c.isRecommended) || categoryComics[0];

  return (
    <>
      <Header />
      <main>
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 pt-24 pb-0 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                  首页
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li>
                <Link to="/category/xuanhuan" className="hover:text-primary-500 transition-colors cursor-pointer">
                  漫画分类
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li className="text-foreground-800 font-medium">{category.name}</li>
            </ol>
          </div>
        </nav>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 pt-8 pb-16 md:pb-20 bg-background-50" aria-labelledby="category-title">
          <div className="max-w-[1400px] mx-auto">
            <span className="inline-block px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-full mb-4">
              <i className={`${category.icon} mr-1`}></i>
              漫画分类
            </span>
            <h1 id="category-title" className="text-3xl md:text-5xl font-heading text-foreground-950">
              {category.name}漫画
            </h1>
            <p className="mt-4 text-base text-foreground-600 max-w-2xl">
              {category.description}。共收录 {category.comicCount.toLocaleString()} 部作品，持续更新中。
            </p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
              <div className="p-4 rounded-lg bg-background-100">
                <div className="text-2xl md:text-3xl font-heading text-primary-500">{category.comicCount.toLocaleString()}</div>
                <p className="text-xs text-foreground-500 mt-1">收录作品</p>
              </div>
              <div className="p-4 rounded-lg bg-background-100">
                <div className="text-2xl md:text-3xl font-heading text-accent-500">每日更新</div>
                <p className="text-xs text-foreground-500 mt-1">更新频率</p>
              </div>
              <div className="p-4 rounded-lg bg-background-100">
                <div className="text-2xl md:text-3xl font-heading text-secondary-600">正版授权</div>
                <p className="text-xs text-foreground-500 mt-1">内容来源</p>
              </div>
              <div className="p-4 rounded-lg bg-background-100">
                <div className="text-2xl md:text-3xl font-heading text-primary-500">高清画质</div>
                <p className="text-xs text-foreground-500 mt-1">阅读品质</p>
              </div>
            </div>
          </div>
        </section>

        {/* Content Grid */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            {featured && (
              <div className="mb-10">
                <h2 className="text-lg font-heading text-foreground-900 mb-5">🌟 编辑推荐</h2>
                <ComicCard {...featured} variant="featured" />
              </div>
            )}

            <h2 className="text-lg font-heading text-foreground-900 mb-5">📚 全部作品</h2>
            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-foreground-500">
                <p>暂无该分类的漫画作品，敬请期待！</p>
              </div>
            )}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-lg font-heading text-foreground-900 mb-5">浏览更多分类</h2>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.chongchongcomic.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '漫画分类',
                item: 'https://www.chongchongcomic.com/category/xuanhuan',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: `${category.name}漫画`,
              },
            ],
          }),
        }}
      />
    </>
  );
}